/* PALISADE CUSTOM =========================================================
   palisade-reveal.js — site-wide scroll-reveal engine.

   Pairs with assets/palisade-reveal.css (the look) and
   snippets/palisade-reveal.liquid (head bootstrap + merchant settings).

   WHAT IT DOES
   Walks every section once, works out which elements inside it are worth
   revealing, and stamps them with `data-reveal="<variant>"` +
   `data-reveal-state`. Siblings sharing a parent are grouped so they cascade
   together off a single IntersectionObserver trigger, which reads as
   choreography rather than a hundred independent fades.

   WHY IT IS SELECTOR-DRIVEN RATHER THAN PER-SECTION MARKUP
   Every Palisade section follows the same BEM shape
   (`palisade-<section>__<part>`), so one ordered rule table covers all of
   them plus the stock Horizon sections — no per-section Liquid edits, and
   nothing to re-apply after an upstream Horizon merge.

   THE ONE RULE THAT MATTERS: CONTENT NEVER STAYS HIDDEN
   - Element not currently rendered at scan time (closed <details>, inactive
     tab panel, other-breakpoint variant)? Left untouched, so it can never be
     a hidden thing waiting on a trigger that will not come.
   - Anything skipped simply has no reveal state, and no state means visible.
   - Focus lands inside something pending, or a <details> opens? Revealed on
     the spot.
   - Engine throws, or IntersectionObserver is missing? `panic()` unveils
     everything.
   - Reduced motion / print / no JS? Handled in CSS, without this file.
   ========================================================================= */

(function () {
  'use strict';

  /* Tells the head bootstrap the engine is alive, so its failsafe leaves the
     pre-boot veil in our hands. Set before anything that could throw. */
  window.palisadeRevealBooted = true;

  var doc = document;
  var root = doc.documentElement;
  var ON_CLASS = 'palisade-reveal-on';
  var STATE = 'data-reveal-state';
  var VARIANT = 'data-reveal';
  var SCANNED = 'data-reveal-section';

  /* The bootstrap's failsafe already fired (engine arrived too late), or the
     visitor prefers reduced motion so the veil was never armed. Either way
     the page is fully visible and there is nothing for us to do. */
  if (!root.classList.contains(ON_CLASS)) return;

  var cfg = window.PalisadeRevealConfig || {};
  var STEP = clamp(cfg.step, 20, 260, 90); // stagger between siblings (ms)
  var DUR = clamp(cfg.dur, 200, 2000, 820); // longest variant duration (ms)
  var INTRO_STEP = Math.round(STEP * 0.72); // brisker cascade on first paint
  var INTRO_CAP = 640; // never hold the first screen longer than this
  var STAGGER_CAP = 9; // items past this share the last delay slot
  var MAX_TARGETS = 700; // whole-page budget, guards pathological pages

  /* ---------------------------------------------------------------------
     Rule table — first match wins, so order is the priority order.
     Repeated-item tokens (`__pillar`, `__item`) sit ABOVE accent tokens
     (`__pill`, `__icon`) because several classes contain both: without
     this, `palisade-pillars__pillar` would be treated as a pill.
     --------------------------------------------------------------------- */
  var RULES = [
    // Author override: data-reveal="<variant>" anywhere in a template.
    ['[data-reveal]', null],

    // Decorative brand rules draw themselves in.
    ['[class*="__flourish"]', 'sweep'],

    // Chart columns grow up from the axis.
    ['[class*="__bar"]', 'bar'],

    // Repeated items — the cascade workhorses. Grid cells are reached through
    // `__item` (every grid in the theme uses it), which is why a bare `__card`
    // is a wrapper instead: it lets the one-off "card" panels — the doctor
    // quote, for instance — cascade their portrait and copy separately rather
    // than fading in as one slab.
    [
      '[class*="__item"],[class*="__row"],[class*="__pillar"],' +
        '[class*="__benefit"],[class*="__study"],[class*="__plan"],[class*="__perk"],' +
        '[class*="__seal"],[class*="__tablist"],[class*="__tabs"],[class*="__menu"],' +
        '[class*="__block"],li,.shopify-block,.group-block,.product-grid__item,' +
        '.resource-list__item',
      'soft',
    ],

    // Kickers and micro-labels lead their group in.
    ['[class*="__eyebrow"],[class*="__label"],[class*="__meta"],[class*="__dose"],[class*="__id"]', 'soft'],

    // Headings get the signature curtain wipe.
    [
      '[class*="__heading"],[class*="__title"],[class*="__headline"],' +
        '[class*="__question"],[class*="__name"],[class*="__quote"]',
      'wipe',
    ],

    // Editorial imagery, hand-placed feel.
    ['[class*="__portrait"],[class*="__card-media"],[class*="__strip"],[class*="__gummies"]', 'tilt'],

    // Everything else visual.
    [
      '[class*="__media"],[class*="__image"],[class*="__img"],[class*="__figure"],' +
        '[class*="__logo"],[class*="__wordmark"],img,picture',
      'media',
    ],

    // Small accents.
    [
      '[class*="__pill"],[class*="__number"],[class*="__icon"],[class*="__toggle"],' +
        '[class*="__badge"],[class*="__cta"],[class*="__arrow"],.button',
      'pop',
    ],

    // Body copy.
    [
      '[class*="__body"],[class*="__lede"],[class*="__subheading"],[class*="__subtext"],' +
        '[class*="__text"],[class*="__copy"],[class*="__excerpt"],[class*="__answer"],' +
        '[class*="__caption"],[class*="__citation"],[class*="__attribution"],' +
        '[class*="__legal"],[class*="__copyright"],[class*="__empty"],' +
        '[class*="__readmore"],[class*="__readlink"],[class*="__detail"],' +
        '.text-block,.rte,p,blockquote',
      'rise',
    ],

    // Stock Horizon fallback: theme blocks render with no wrapper of their
    // own, so reach them through the containers Horizon does emit. Also
    // catches the hero, whose copy is all `content_for 'blocks'`.
    ['.section-content-wrapper > *,.group-block-content > *,.palisade-hero__content > *', 'rise'],
  ];

  var UNION = RULES.map(function (rule) {
    return rule[0];
  }).join(',');

  /* ---------------------------------------------------------------------
     Never a target. Two kinds:
     1. Structural wrappers — skipped so their CHILDREN animate instead,
        which is what turns a grid into a cascade.
     2. Decorative backdrops and utility nodes — either owned by the
        parallax layer / their section's own CSS, or invisible anyway.

     `__head` is matched by suffix, not substring: `[class*="__head"]` would
     also swallow every `__heading` and kill all the heading reveals.
     --------------------------------------------------------------------- */
  var NEVER = [
    '[' + STATE + ']', // already processed in an earlier pass
    '[data-no-reveal]',
    '.visually-hidden',
    '.skip-to-content-link',
    '.palisade-anchor',
    '.section-background',
    // structural wrappers. `__head` and `__card` are matched as whole tokens,
    // not substrings: `[class*="__head"]` would swallow every `__heading` and
    // `[class*="__card"]` every `__card-media` / `__card-title`.
    '[class$="__head"]',
    '[class*="__head "]',
    '[class$="__card"]',
    '[class*="__card "]',
    /* Mixed-type heading FRAGMENTS — `__heading-serif` / `__title-sans` and the
       like. The heading union above matches by substring, so a heading built from
       two spans handed a reveal to the <h2> AND to each span: three nested
       clip-path + opacity layers over one line of text, which Chrome rasterises
       at layer resolution and composites — the text renders visibly soft, and any
       repaint nearby (hovering a footer link) re-triggers it. They also wiped
       independently of each other, which the design never asks for.

       The trailing hyphen is what makes this safe: it matches only the fragment
       classes, never `__heading` / `__title` itself, so the heading keeps its own
       reveal and animates as one unit. Covers footer, blog, ingredients, science
       and faq, and any section that adopts the same two-span pattern later. */
    '[class*="__heading-"]',
    '[class*="__title-"]',
    '[class*="__headline-"]',
    '[class*="__inner"]',
    '[class*="__content"]',
    '[class*="__header"]',
    '[class*="__list"]',
    '[class*="__cards"]',
    '[class*="__grid"]',
    '[class*="__items"]',
    '[class*="__icons"]',
    '[class*="__pills"]',
    '[class*="__benefits"]',
    '[class*="__perks"]',
    '[class*="__plans"]',
    '[class*="__menus"]',
    '[class*="__panel"]', // covers __panel and __panels
    '[class*="__accordions"]',
    '[class*="__callouts"]',
    '[class*="__rings"]',
    '[class*="__blocks"]',
    '[class*="__intro"]',
    '[class*="__summary"]',
    '[class*="__top"]',
    '[class*="__stage"]',
    '[class*="__graph"]',
    '[class*="__chart"]',
    '[class*="__col"]',
    '[class*="__form"]', // also keeps dynamic form status messages visible
    '[class*="__tab-indicator"]',
    '[class*="__pagination"]',
    '[class*="__thumbs"]',
    '[class*="__gallery"]', // sticky on desktop — leave its transform alone
    '[class*="__details"]',
    // decorative backdrops
    '[class*="__bg"]',
    '[class*="__wash"]',
    '[class*="__scrim"]',
    '[class*="__texture"]',
    '[class*="__placeholder"]',
  ].join(',');

  /* ---------------------------------------------------------------------
     Nothing inside these is ever touched.
     - Site chrome and overlays open/close on their own schedule, and a
       transform on an ancestor of a fixed-position child would break its
       positioning outright.
     - `[data-pkb-animate]` is the key-benefits section, which ships its own
       heartbeat choreography. Turn that setting off and this engine picks
       the section up automatically.
     - `__form-component` is the buy box's purchase path (plan picker, price,
       add to cart). Never delay or transform the thing people came to click.
     --------------------------------------------------------------------- */
  var SKIP_ANCESTORS = [
    '[data-no-reveal]',
    '[data-pkb-animate]',
    '#header-group',
    'header-component',
    'dialog',
    '[popover]',
    'cart-drawer-component',
    'sticky-add-to-cart',
    '.quick-add-modal',
    '#chat-drawer',
    'template',
    'noscript',
    'svg',
    '[class*="__form-component"]',
  ].join(',');

  var io = null;
  var groupOf = new WeakMap(); // observed element -> group
  var watching = []; // groups still waiting on a scroll trigger
  var targetCount = 0;
  var vh = window.innerHeight || 800;

  /* PALISADE CUSTOM — end-of-scroll sweep state. These MUST be initialised
     here, above the boot block, not beside the functions that use them further
     down. `var` hoists the declaration but NOT the assignment, and the boot
     `try` below calls installSafetyNets() -> watchForEndOfScroll() ->
     stopEndSweep() long before any statement lower in this IIFE has run. With
     the initialisers further down, endSweepTargets was `undefined` at that
     point, stopEndSweep() threw on `.length`, the boot catch called panic(),
     and panic() unveils the entire page — which silently removed every scroll
     reveal on the site. Shipped and reverted 2026-09-02; do not move these. */
  var END_SLACK = 2; // px of tolerance for sub-pixel scroll rounding
  var endSweepQueued = false;
  var endSweepTargets = [];

  /* ===================================================================
     Boot
     =================================================================== */
  try {
    if (!('IntersectionObserver' in window)) {
      panic();
      return;
    }

    io = new IntersectionObserver(onIntersect, {
      // Fire a little before the element reaches the very bottom edge, so the
      // motion has begun by the time it is properly in view.
      rootMargin: '0px 0px -12% 0px',
      threshold: 0,
    });

    scan(sectionsIn(doc), true);
    installSafetyNets();
  } catch (error) {
    panic();
  }

  /* ===================================================================
     Scanning
     =================================================================== */

  function sectionsIn(scope) {
    var out = [];
    var roots = [];

    if (scope === doc) {
      // Only main content and the footer. The header group, cart drawer and
      // every overlay live outside these and are intentionally left alone.
      var main = doc.getElementById('MainContent');
      if (main) roots.push(main);
      var footer = doc.querySelector('.page-wrapper > footer') || doc.querySelector('footer');
      if (footer) roots.push(footer);
    } else {
      // `shopify:section:load` hands us the section element itself.
      if (scope.classList && scope.classList.contains('shopify-section') && !scope.hasAttribute(SCANNED)) {
        out.push(scope);
      }
      roots.push(scope);
    }

    roots.forEach(function (node) {
      var found = node.querySelectorAll('.shopify-section:not([' + SCANNED + '])');
      for (var i = 0; i < found.length; i++) out.push(found[i]);
    });
    return out;
  }

  /**
   * Scans a batch of sections. Deliberately split into a read phase and a
   * write phase: interleaving `getClientRects()` with `setAttribute()` would
   * force a style recalc per element, which on a long page is the difference
   * between one layout pass and several hundred.
   *
   * @param {Element[]} sections
   * @param {boolean} [isInitial] - true for the first-paint pass, which also
   *   runs the above-the-fold intro cascade.
   */
  function scan(sections, isInitial) {
    if (!sections.length) return;
    vh = window.innerHeight || vh;

    // Write: lift the veil first, so a throw further down still leaves every
    // section visible.
    sections.forEach(function (section) {
      section.setAttribute(SCANNED, '');
    });

    // Read: collect targets and measure, touching nothing.
    var accepted = new Set();
    var items = [];
    sections.forEach(function (section) {
      try {
        collect(section, accepted, items);
      } catch (error) {
        /* One awkward section must not stop the rest. It stays visible. */
      }
    });
    if (!items.length) return;

    var groups = build(items);
    var plan = [];
    groups.forEach(function (group) {
      var rect = group.observed.getBoundingClientRect();
      plan.push({
        group: group,
        // Above the fold, or scrolled past already (reload / anchor / restored
        // scroll position) — either way it must not wait for a scroll event.
        immediate: rect.top < vh,
        past: rect.bottom <= 0,
      });
    });

    // Write: states, then observers.
    items.forEach(function (item) {
      item.el.setAttribute(VARIANT, item.variant);
      item.el.setAttribute(STATE, 'pending');
    });

    var intro = [];
    plan.forEach(function (entry) {
      if (entry.past) {
        // Off-screen above before we even started: snap it, no point animating
        // something nobody can see, and no point on the timers either.
        revealGroup(entry.group, 0, 0, true);
      } else if (entry.immediate && isInitial) {
        intro.push(entry.group);
      } else if (entry.immediate) {
        // A section injected into the current viewport after load (theme
        // editor, section rendering) should just play, not sit and wait.
        revealGroup(entry.group, STEP, 0);
      } else {
        groupOf.set(entry.group.observed, entry.group);
        watching.push(entry.group);
        io.observe(entry.group.observed);
      }
    });

    if (intro.length) runIntro(intro);
  }

  /* -------------------------------------------------------------------
     PALISADE CUSTOM 2026-09-01 — the end-of-document dead band.

     The observer's `-12%` bottom margin shrinks its box to `0 .. vh - 0.12vh`.
     Everywhere in the middle of a page that is just lead-in: scrolling lifts an
     element out of the excluded band. At the very END of the document it is a
     trap — there is no scroll left to lift anything, so any group parked in the
     final 12% of a viewport height can NEVER intersect, stays `pending`, and
     `pending` is opacity 0 for good. That breaks this engine's one promise.

     It was reported as "the footer policy links are missing on my phone".
     Measured at 390x844: `.palisade-footer__legal` (the copyright + Privacy /
     Refund / Terms / Shipping / Contact row, the last in-flow element on every
     page) sits at y 784-838 with the page already at max scroll, while the
     observation box ends at 743. At max scroll it was the ONLY element left
     pending on the whole page. The band scales with the viewport (0.12 x vh:
     80px at 667, 101px at 844, 112px at 932), which is why it strands on a
     phone and not on a 900px desktop window.

     The fix reuses reconcile() rather than adding new reveal logic: it already
     tests plain full-viewport geometry with no dead band, already evicts
     detached groups, and already no-ops once nothing is watched. It just had no
     caller after load. Gating the sweep on "the scroller is at its end" is what
     keeps the -12% lead-in intact everywhere else — mid-page scrolling does not
     trigger it, so nothing reveals earlier than it does today.

     Verified in the live page before shipping: at max scroll the stranded row
     went pending/opacity 0 -> in/opacity 1 on a single reconcile() call.
     ------------------------------------------------------------------- */

  /**
   * The box that actually scrolls. At >=990px assets/base.css locks html/body
   * to `overflow: hidden` and gives `.page-wrapper` `overflow-y: auto`, so the
   * document reports a stale non-zero "remaining" that the visitor can never
   * consume. Reading the CSS that causes that — rather than re-matching
   * `(min-width: 990px)` — keeps this correct if the breakpoint ever moves, and
   * on a layout with no wrapper at all (layout/password.liquid).
   * Verified both ways: at 1440px the wrapper reports overflow-y `auto` with
   * scrollHeight 9691 > clientHeight 900; at 390px it reports `visible` with
   * scrollHeight === clientHeight and the document scrolls instead.
   */
  function scrollerBox() {
    var wrapper = doc.querySelector('.page-wrapper');
    if (wrapper) {
      var overflowY = window.getComputedStyle(wrapper).overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll') return wrapper;
    }
    return doc.scrollingElement || doc.documentElement;
  }

  /** Scroll left in the real scroller before it bottoms out. */
  function scrollRemaining() {
    var box = scrollerBox();
    if (!box) return Infinity;
    return box.scrollHeight - box.clientHeight - box.scrollTop;
  }

  /**
   * Sweep only at the end of the scroll range, which is the only place the
   * observer's band is unreachable. reconcile() reveals a group solely when its
   * rect is genuinely within the viewport, so even if scrollerBox() ever picked
   * the wrong box this degrades to "reveals what is on screen" — never to
   * snapping the whole page visible.
   */
  function sweepIfAtEnd() {
    endSweepQueued = false;
    if (!watching.length) return stopEndSweep();
    if (scrollRemaining() > END_SLACK) return;
    reconcile();
    if (!watching.length) stopEndSweep();
  }

  function queueEndSweep() {
    if (endSweepQueued) return;
    endSweepQueued = true;
    requestAnimationFrame(sweepIfAtEnd);
  }

  function stopEndSweep() {
    for (var i = 0; i < endSweepTargets.length; i++) {
      endSweepTargets[i].removeEventListener('scroll', queueEndSweep);
    }
    endSweepTargets = [];
    window.removeEventListener('resize', queueEndSweep);
  }

  /**
   * Listen on BOTH candidates rather than resolving the scroller once: a
   * resize can cross the 990px breakpoint and hand the job to the other box
   * without a reload. Coalesced into one rAF, self-removing the moment nothing
   * is left pending, so a fully revealed page carries no listeners at all.
   */
  function watchForEndOfScroll() {
    // Idempotent: unbind first so repeat calls (boot, load, a theme-editor
    // section re-render) can never stack duplicate listeners, and so the
    // wrapper reference is re-resolved if the layout swapped it out.
    stopEndSweep();
    if (!watching.length) return;

    var wrapper = doc.querySelector('.page-wrapper');
    endSweepTargets = wrapper ? [window, wrapper] : [window];
    for (var i = 0; i < endSweepTargets.length; i++) {
      endSweepTargets[i].addEventListener('scroll', queueEndSweep, { passive: true });
    }
    window.addEventListener('resize', queueEndSweep, { passive: true });

    // A page short enough to have no scroll range at all is already "at the
    // end", and would otherwise never fire a scroll event to find that out.
    queueEndSweep();
  }

  /**
   * Catches groups that ended up on screen through something other than a
   * scroll event: a #fragment landing, a browser-restored scroll position, or
   * layout shifting under them as images finish loading. The observer's
   * `-12%` bottom margin is what makes the timing feel right while scrolling,
   * but it also means an element parked in that band is not "intersecting" —
   * so without this pass it would sit there invisible until something moved.
   */
  function reconcile() {
    if (!watching.length) return;
    var height = window.innerHeight || vh;
    var still = [];
    for (var i = 0; i < watching.length; i++) {
      var group = watching[i];
      if (group.done) continue;
      // Detached with its section (e.g. Section Rendering API swap, which
      // fires no shopify:section:unload) → evict instead of watching forever.
      if (!group.observed.isConnected) {
        io.unobserve(group.observed);
        continue;
      }
      var rect = group.observed.getBoundingClientRect();
      if (rect.top < height && rect.bottom > 0) {
        io.unobserve(group.observed);
        revealGroup(group, STEP, 0);
      } else {
        still.push(group);
      }
    }
    watching = still;
  }

  /**
   * Collects reveal targets inside one section, outermost-wins.
   * @param {Element} section
   * @param {Set<Element>} accepted - shared across the batch for ancestor checks
   * @param {Array} items - output
   */
  function collect(section, accepted, items) {
    var candidates = section.querySelectorAll(UNION);

    for (var i = 0; i < candidates.length; i++) {
      if (targetCount >= MAX_TARGETS) return;
      var el = candidates[i];
      var authored = el.getAttribute(VARIANT);

      // Explicit authoring outranks the structural skip list.
      if (!authored && el.matches(NEVER)) continue;
      if (el.closest(SKIP_ANCESTORS)) continue;

      // Not rendered right now: a closed <details>, an inactive tab panel, or
      // the other breakpoint's copy of an element. Leaving it alone is the
      // whole reason those never end up permanently invisible.
      if (!isRendered(el)) continue;

      // Document order guarantees ancestors are seen first, so this keeps the
      // outermost match and drops its descendants — a card animates as one
      // piece rather than fighting its own heading.
      if (hasAcceptedAncestor(el, accepted)) continue;

      var variant = authored || variantFor(el);
      if (!variant) continue;

      // `tilt` rotates, and a rotated element as wide as the viewport pushes
      // its corners past the edge and adds a horizontal scrollbar. Narrow
      // editorial art keeps the tilt; anything close to full-bleed falls back
      // to the straight settle.
      if (variant === 'tilt' && el.getBoundingClientRect().width > (window.innerWidth || 1440) * 0.7) {
        variant = 'media';
      }

      accepted.add(el);
      items.push({ el: el, variant: variant, parent: el.parentElement });
      targetCount++;
    }
  }

  /**
   * Is this element actually being painted right now? Anything that is not
   * must be left untouched — an element with no reveal state is visible, while
   * a pending element whose trigger can never fire is invisible forever.
   *
   * `getClientRects()` alone is not enough: a closed <details> in Chromium
   * keeps layout boxes for its contents (`content-visibility: hidden`) but
   * never paints them and never satisfies an IntersectionObserver, which is
   * precisely the shape of the bug this guards against.
   */
  function isRendered(el) {
    if (!el.getClientRects().length) return false;

    var details = el.closest('details');
    if (details && !details.open && el !== details && !el.closest('summary')) return false;

    // Newer engines can answer this directly, including content-visibility.
    if (typeof el.checkVisibility === 'function' && !el.checkVisibility()) return false;

    return true;
  }

  function variantFor(el) {
    for (var i = 0; i < RULES.length; i++) {
      var variant = RULES[i][1];
      if (variant && el.matches(RULES[i][0])) return variant;
    }
    return null;
  }

  function hasAcceptedAncestor(el, accepted) {
    var node = el.parentElement;
    while (node) {
      if (accepted.has(node)) return true;
      node = node.parentElement;
    }
    return false;
  }

  /**
   * Buckets targets by parent so siblings cascade off one trigger.
   * @param {Array} items
   * @returns {Array} groups, in document order
   */
  function build(items) {
    var buckets = new Map();
    items.forEach(function (item) {
      var key = item.parent || item.el;
      var bucket = buckets.get(key);
      if (!bucket) {
        bucket = [];
        buckets.set(key, bucket);
      }
      bucket.push(item);
    });

    var groups = [];
    buckets.forEach(function (bucket, parent) {
      // Observe the element itself when it is alone (more precise than
      // waiting on its wrapper), or when the wrapper is taller than most of
      // the viewport — there, one trigger would fire long before the items
      // near its bottom are anywhere near being seen.
      var perItem = bucket.length === 1 || parent.getBoundingClientRect().height > vh * 0.85;
      if (perItem) {
        bucket.forEach(function (item) {
          groups.push({ observed: item.el, items: [item] });
        });
      } else {
        groups.push({ observed: parent, items: bucket });
      }
    });

    groups.sort(function (a, b) {
      var relation = a.observed.compareDocumentPosition(b.observed);
      if (relation & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
      if (relation & Node.DOCUMENT_POSITION_PRECEDING) return 1;
      return 0;
    });

    return groups;
  }

  /* ===================================================================
     Revealing
     =================================================================== */

  function onIntersect(entries) {
    for (var i = 0; i < entries.length; i++) {
      var entry = entries[i];
      var target = entry.target;

      if (!entry.isIntersecting) {
        // Already scrolled past — above the viewport, never coming back down
        // through it, so its scroll trigger will never fire. This is the case
        // that matters: landing on a #fragment, a restored scroll position, or
        // any programmatic scroll that beat the initial scan. Without this the
        // content above the landing point stays invisible for good.
        var rect = entry.boundingClientRect;
        var top = entry.rootBounds ? entry.rootBounds.top : 0;
        if (rect.bottom <= top) {
          io.unobserve(target);
          revealPast(target);
        }
        continue;
      }

      io.unobserve(target);
      var group = groupOf.get(target);
      if (group) revealGroup(group, STEP, 0);
    }
  }

  /** Snaps an off-screen-above group visible. Animating it would be wasted. */
  function revealPast(target) {
    var group = groupOf.get(target);
    if (group) revealGroup(group, 0, 0, true);
  }

  /**
   * @param {object} group
   * @param {number} step - stagger between siblings
   * @param {number} startDelay - offset for the whole group
   * @param {boolean} [snap] - skip the animation entirely
   * @param {number} [cap] - ceiling on any single element's delay
   */
  function revealGroup(group, step, startDelay, snap, cap) {
    if (group.done) return;
    group.done = true;
    for (var i = 0; i < group.items.length; i++) {
      if (snap) {
        snapVisible(group.items[i].el);
      } else {
        var delay = startDelay + Math.min(i, STAGGER_CAP) * step;
        reveal(group.items[i].el, cap ? Math.min(delay, cap) : delay);
      }
    }
  }

  function reveal(el, delay) {
    if (el.getAttribute(STATE) !== 'pending') return;
    if (delay > 0) el.style.setProperty('--reveal-delay', delay + 'ms');
    el.setAttribute(STATE, 'in');
    // Settle explicitly rather than on `transitionend`: an element hidden
    // part-way through (tab switch, accordion close) would never fire the
    // event, and `done` is what drops the clip-path so it can no longer trim
    // a focus ring.
    // Slack covers the variants that run longer than the base duration (sweep
    // at 1100ms is the longest). Landing late is harmless; landing early would
    // cut the animation short.
    window.setTimeout(function () {
      el.setAttribute(STATE, 'done');
      el.style.removeProperty('--reveal-delay');
    }, delay + DUR + 800);
  }

  /**
   * Jumps straight to fully visible with no transition at all — every safety
   * net uses this. Fading in over most of a second would leave a focused
   * element half-transparent while it has focus, which is not a visible focus
   * indicator (WCAG 2.4.7), and would make a merchant watch their selected
   * block fade up before they can see what they are editing.
   */
  function snapVisible(el) {
    if (!el || el.getAttribute(STATE) === 'done') return;
    el.style.removeProperty('--reveal-delay');
    el.setAttribute(STATE, 'done');
  }

  /**
   * First-paint cascade for whatever is already on screen. Two frames so the
   * pending state is actually painted before we transition away from it.
   */
  function runIntro(groups) {
    var played = false;
    var play = function () {
      if (played) return;
      played = true;
      var delay = 0;
      groups.forEach(function (group) {
        // INTRO_CAP bounds the whole cascade, not just each group's start, so a
        // busy first screen (a grid above the fold, say) cannot turn the intro
        // into a queue the visitor has to wait out.
        revealGroup(group, INTRO_STEP, Math.min(delay, INTRO_CAP), false, INTRO_CAP);
        delay += group.items.length * INTRO_STEP;
      });
    };

    requestAnimationFrame(function () {
      requestAnimationFrame(play);
    });
    // Backstop: rAF is paused in a background tab, and a visitor who never
    // focuses the tab still deserves a page.
    window.setTimeout(play, 1600);
  }

  /* ===================================================================
     Safety nets
     =================================================================== */

  function installSafetyNets() {
    // Two reconcile passes: once everything has loaded and settled, and once
    // more shortly after to absorb a late fragment scroll or a slow image.
    window.addEventListener('load', function () {
      reconcile();
      window.setTimeout(reconcile, 600);
      // Re-arm after load: the page has its final height here, so a group that
      // late images pushed into the end-of-document dead band is caught too.
      watchForEndOfScroll();
    });

    // PALISADE CUSTOM — sweep anything the observer's bottom band cannot reach
    // once the scroller bottoms out. See the note above reconcile().
    watchForEndOfScroll();

    // Focus must never land on something invisible (WCAG 2.4.7 / 2.4.11).
    doc.addEventListener(
      'focusin',
      function (event) {
        revealAncestors(event.target);
      },
      true
    );

    // An accordion opening reveals content that was not rendered at scan
    // time; if it WAS rendered and is still pending, show it now.
    doc.addEventListener(
      'toggle',
      function (event) {
        var el = event.target;
        if (!el || el.tagName !== 'DETAILS' || !el.open) return;
        revealWithin(el);
      },
      true
    );

    // Theme editor: re-scan re-rendered sections, and never let a merchant
    // select a block they cannot see. The scanned flag is cleared first because
    // the editor swaps a section's contents while keeping its wrapper — without
    // that, the freshly rendered markup would never be looked at. Clearing and
    // re-tagging happen in the same task, so nothing flashes.
    doc.addEventListener('shopify:section:load', function (event) {
      var section = event.target;
      if (section && section.removeAttribute) section.removeAttribute(SCANNED);
      scan(sectionsIn(section), false);
    });

    // Evict groups whose observed element left the document with its section —
    // otherwise `watching` keeps strong references to the detached subtree and
    // reconcile() can never release them (a detached rect is all zeros, so the
    // group is pushed back into `still` forever). Deferred a tick: the unload
    // event fires while the section is still connected (it has to, to bubble),
    // so a synchronous sweep would be one event-cycle late.
    doc.addEventListener('shopify:section:unload', function () {
      setTimeout(function () {
        var still = [];
        for (var i = 0; i < watching.length; i++) {
          var group = watching[i];
          if (group.observed && group.observed.isConnected) {
            still.push(group);
          } else if (io) {
            io.unobserve(group.observed);
          }
        }
        watching = still;
      }, 0);
    });
    ['shopify:section:select', 'shopify:section:reorder', 'shopify:block:select'].forEach(function (name) {
      doc.addEventListener(name, function (event) {
        revealWithin(event.target);
        revealAncestors(event.target);
      });
    });

    // Sections injected without a Shopify event (section rendering API).
    if ('MutationObserver' in window) {
      var queued = [];
      var flush = function () {
        var batch = queued;
        queued = [];
        if (batch.length) scan(batch, false);
      };
      var observer = new MutationObserver(function (records) {
        var before = queued.length;
        for (var r = 0; r < records.length; r++) {
          var added = records[r].addedNodes;
          for (var n = 0; n < added.length; n++) {
            var node = added[n];
            if (node.nodeType !== 1) continue;
            if (node.classList && node.classList.contains('shopify-section') && !node.hasAttribute(SCANNED)) {
              queued.push(node);
            } else if (node.querySelectorAll) {
              var found = node.querySelectorAll('.shopify-section:not([' + SCANNED + '])');
              for (var i = 0; i < found.length; i++) queued.push(found[i]);
            }
          }
        }
        if (!before && queued.length) requestAnimationFrame(flush);
      });

      ['#MainContent', '.page-wrapper > footer'].forEach(function (selector) {
        var node = doc.querySelector(selector);
        if (node) observer.observe(node, { childList: true, subtree: true });
      });
    }

    // Debug / escape hatch for anyone poking at this in the console.
    window.PalisadeReveal = {
      rescan: function () {
        scan(sectionsIn(doc), false);
      },
      revealAll: function () {
        revealWithin(doc.body);
      },
      reconcile: reconcile,
      panic: panic,
    };
  }

  /** Reveals every pending element inside a subtree, immediately. */
  function revealWithin(el) {
    if (!el || !el.querySelectorAll) return;
    var section = el.closest ? el.closest('.shopify-section') : null;
    if (section && !section.hasAttribute(SCANNED)) section.setAttribute(SCANNED, '');
    if (el.getAttribute && el.getAttribute(STATE) === 'pending') snapVisible(el);
    var pending = el.querySelectorAll('[' + STATE + '="pending"]');
    for (var i = 0; i < pending.length; i++) snapVisible(pending[i]);
  }

  /** Reveals every pending ancestor of an element, immediately. */
  function revealAncestors(el) {
    if (!el || !el.closest) return;
    var section = el.closest('.shopify-section');
    if (section && !section.hasAttribute(SCANNED)) section.setAttribute(SCANNED, '');
    var node = el.closest('[' + STATE + '="pending"]');
    while (node) {
      snapVisible(node);
      node = node.parentElement ? node.parentElement.closest('[' + STATE + '="pending"]') : null;
    }
  }

  /**
   * Last resort. Drops the veil and every pending state, leaving a plain,
   * fully visible page. Better a page with no animation than a blank one.
   */
  function panic() {
    try {
      root.classList.remove(ON_CLASS);
      var sections = doc.querySelectorAll('.shopify-section:not([' + SCANNED + '])');
      for (var i = 0; i < sections.length; i++) sections[i].setAttribute(SCANNED, '');
      var pending = doc.querySelectorAll('[' + STATE + '="pending"]');
      for (var j = 0; j < pending.length; j++) pending[j].setAttribute(STATE, 'done');
    } catch (error) {
      /* Nothing left to try. */
    }
  }

  /* ===================================================================
     Utilities
     =================================================================== */

  function clamp(value, min, max, fallback) {
    var number = parseFloat(value);
    if (!isFinite(number)) return fallback;
    return Math.min(max, Math.max(min, number));
  }
})();
